import React, { useEffect, useRef, useState } from 'react';
import { formatDublinDate } from '../../utils/date';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CameraIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useListChildMomentsQuery } from '../../store/api/localApi';
import { ChildMomentSummary } from '../../types';
import ClickablePhoto from '../../components/parent/ClickablePhoto';

/**
 * /parent/:studentId/moments — the child's Moments album: a photo-first grid of
 * every moment staff have shared, newest first, with cursor-based infinite
 * scroll. Also serves /parent/:studentId/moments/:momentId (the moment-detail
 * deep-link target): same album, with the linked moment scrolled into view and
 * briefly ring-highlighted. There is no heavier single-moment page — a moment
 * is just its photos + caption, which the card already shows in full.
 */
const ParentMomentsPage: React.FC = () => {
  const { momentId } = useParams<{ momentId?: string }>();
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  // An unresolved child: a per-child album needs a child. Send the parent home.
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  return (
    <MomentsAlbum
      studentId={activeStudent.id}
      childFirstName={activeStudent.firstName}
      highlightMomentId={momentId}
    />
  );
};

const MomentsAlbum: React.FC<{
  studentId: string;
  childFirstName?: string;
  highlightMomentId?: string;
}> = ({ studentId, childFirstName, highlightMomentId }) => {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [moments, setMoments] = useState<ChildMomentSummary[]>([]);
  const mergedCursors = useRef<Set<string>>(new Set());

  useEffect(() => {
    setCursor(undefined);
    setMoments([]);
    mergedCursors.current = new Set();
  }, [studentId]);

  const { data, isFetching, isLoading } = useListChildMomentsQuery({ childId: studentId, cursor });

  useEffect(() => {
    if (!data) return;
    const key = cursor ?? '';
    if (mergedCursors.current.has(key)) return;
    mergedCursors.current.add(key);
    setMoments((prev) => [...prev, ...data.moments]);
  }, [data, cursor]);

  const nextCursor = data?.nextCursor;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextCursor || isFetching) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setCursor(nextCursor);
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, isFetching]);

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <Link
            to={`/parent/${studentId}`}
            data-testid="parent-moments-back"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to {childFirstName || 'home'}
          </Link>
        </div>

        <header className="flex items-center gap-2">
          <CameraIcon aria-hidden="true" className="h-5 w-5 text-pink-600" />
          <h1 className="text-lg font-semibold text-gray-900">Moments</h1>
        </header>

        <section data-testid="parent-moments-page">
          {isLoading && moments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-500">
              Loading…
            </div>
          ) : moments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
              No moments shared yet
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {moments.map((moment) => (
                <MomentTile
                  key={moment.id}
                  moment={moment}
                  highlighted={moment.id === highlightMomentId}
                />
              ))}
            </ul>
          )}

          {nextCursor && (
            <div
              ref={sentinelRef}
              data-testid="parent-moments-more"
              className="h-8 flex items-center justify-center text-xs text-gray-400 mt-3"
            >
              {isFetching ? 'Loading more…' : ''}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const MomentTile: React.FC<{ moment: ChildMomentSummary; highlighted?: boolean }> = ({
  moment,
  highlighted,
}) => {
  const ref = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  return (
    <li
      ref={ref}
      data-testid={`parent-moment-${moment.id}`}
      className={`bg-white rounded-lg shadow p-3 ${highlighted ? 'ring-2 ring-pink-400' : ''}`}
    >
      {moment.photoUrls.length > 0 && (
        <div className="grid grid-cols-2 gap-1 mb-2">
          {moment.photoUrls.map((url, idx) => (
            <ClickablePhoto
              key={idx}
              url={url}
              alt={moment.caption || 'Moment photo'}
              downloadName={`moment-${moment.createdAt.slice(0, 10)}`}
              className="w-full h-32 rounded-md"
            />
          ))}
        </div>
      )}
      {moment.caption && (
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{moment.caption}</p>
      )}
      <p className="text-xs text-gray-500 mt-1">
        {moment.authorName}
        {' · '}
        {formatDublinDate(moment.createdAt)}
      </p>
    </li>
  );
};

export default ParentMomentsPage;
